import { FC, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { LongreadBlockWrapper } from '../../styles/LongreadBlockRenderer.styles';
import {
  PodcastWrapper,
  PodcastContent,
  PodcastTitle,
  PodcastDuration,
  PodcastListenButton,
  PodcastIframeWrapper,
  PodcastPlayer,
  PodcastPlayerControls,
  PodcastPlayerButton,
  PodcastPlayerProgress,
  PodcastPlayerTime,
} from '../../styles/LongreadBlockRenderer.styles';
import { FormatLinkedText } from 'brg-longread';
import { invertColor } from '../../utils/colorUtils';
import { Endpoints } from '@brg/api';

interface PodcastBlockProps {
  block: any;
  backgroundColor?: string;
}

const getAdditionalInfoVal = (additionalInfo: any[], key: string): string | null => {
  if (!Array.isArray(additionalInfo)) return null;
  return additionalInfo.find((item: any) => item.name === key)?.value ?? null;
};

const checkAudioType = (link: string): 'sc' | 'bc' | 'mb' | 'nodata' => {
  if (!link) return 'nodata';
  if (/bandcamp/i.test(link)) return 'bc';
  if (/sound/i.test(link)) return 'sc';
  if (/mubert/i.test(link)) return 'mb';
  return 'nodata';
};

const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};


const PlayIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 2l10 6-10 6V2z" />
  </svg>
);

const PauseIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 2h3v12H4V2zm5 0h3v12H9V2z" />
  </svg>
);


export const PodcastBlock: FC<PodcastBlockProps> = ({ block, backgroundColor }) => {
  const [iframeError, setIframeError] = useState(false);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const defaultTextColor = useMemo(
    () => (!backgroundColor ? '#FEFEFE' : invertColor(backgroundColor, '#FEFEFE', '#151514')),
    [backgroundColor]
  );

  const hasIframe = useMemo(
    () => Boolean(block.text?.includes('<iframe')),
    [block.text]
  );

  const { iframeUrl, videoType } = useMemo(() => {
    if (!hasIframe || !block.text) return { iframeUrl: null, videoType: 'nodata' as const };
    const match = block.text.match(/<iframe\s+.*?\s+src=(".*?").*?<\/iframe>/);
    return {
      iframeUrl: match ? match[1].slice(1, -1) : null,
      videoType: checkAudioType(block.text),
    };
  }, [block.text, hasIframe]);

  const title = useMemo((): string => {
    if (hasIframe && block.text) {
      const textWithoutIframe = block.text.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');

      // 1) <a title="Название трека">
      const linkMatches = textWithoutIframe.match(/<a[^>]*title=["']([^"']+)["'][^>]*>/gi);
      if (linkMatches?.length) {
        const lastMatch = linkMatches[linkMatches.length - 1].match(/title=["']([^"']+)["']/);
        if (lastMatch?.[1]) return lastMatch[1].trim();
      }

      // 2) Текст внутри последней <a>...</a>
      const linkTextMatches = textWithoutIframe.match(/<a[^>]*>([^<]+)<\/a>/gi);
      if (linkTextMatches?.length) {
        const textMatch = linkTextMatches[linkTextMatches.length - 1].match(/>([^<]+)</);
        if (textMatch?.[1]) return textMatch[1].trim();
      }

      // 3) Весь текст без HTML
      return textWithoutIframe.replace(/<[^>]*>/g, '').trim();
    }

    if (block.attachments?.length) {
      const att = block.attachments.find((a: any) => a.files?.length);
      if (att) {
        const raw = att.text || att.description || '';
        return raw.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
      }
    }

    return '';
  }, [block.text, block.attachments, hasIframe]);

  const audioUrl = useMemo((): string => {
    if (hasIframe || !block.attachments?.length) return '';

    const files = block.attachments[0]?.files;
    if (!files?.length) return '';

    const mp3File = files.find(
      (f: any) => f?.url?.split('.').pop()?.toLowerCase() === 'mp3'
    );
    let baseUrl: string = (mp3File || files[0])?.url;
    if (!baseUrl) return '';

    if (/\.wav$/i.test(baseUrl)) baseUrl = baseUrl.replace(/\.wav$/i, '.mp3');

    const ext = baseUrl.split('.').pop()?.toLowerCase();
    if (!['mp3', 'wav'].includes(ext || '')) return '';

    return `${Endpoints.files}/${baseUrl}`;
  }, [block.attachments, hasIframe]);

  const podcastTime = useMemo((): string | null => {
    const val = getAdditionalInfoVal(block.additionalInfo || [], 'podcastTime');
    if (!val) return null;
    const total = Math.ceil(Number(val) / 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
  }, [block.additionalInfo]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl || !isPlayerVisible) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    if (audio.dataset.loadedUrl !== audioUrl) {
      audio.dataset.loadedUrl = audioUrl;
      audio.src = audioUrl;
      audio.load();
    }

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl, isPlayerVisible]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPlayerVisible) return;

    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [isPlaying, isPlayerVisible]);


  const handleListenClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlayerVisible(true);
    setIsPlaying(true);
  }, []);

  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
  }, []);

  const handleIframeError = useCallback(() => setIframeError(true), []);
  const handleIframeRetry = useCallback(() => setIframeError(false), []);

  if (hasIframe) {
    if (!iframeUrl) return null;

    let iframeHeight = 300;
    if (videoType === 'bc') iframeHeight = 120;
    else if (videoType === 'mb') iframeHeight = 380;
    else if (videoType === 'sc') {
      const scReg = block.text?.match(/height=["']\s*(\d+)["']/);
      if (scReg) iframeHeight = Number(scReg[1]);
    }

    if (iframeError) {
      return (
        <LongreadBlockWrapper>
          <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
            <div style={{ color: defaultTextColor, padding: '20px', textAlign: 'center' }}>
              При загрузке контента произошла ошибка
              <button
                onClick={handleIframeRetry}
                style={{
                  marginTop: '10px',
                  display: 'block',
                  padding: '8px 16px',
                  border: `1px solid ${defaultTextColor}`,
                  background: 'transparent',
                  color: defaultTextColor,
                  cursor: 'pointer',
                  borderRadius: '4px',
                }}
              >
                Попробовать ещё раз
              </button>
            </div>
          </PodcastWrapper>
        </LongreadBlockWrapper>
      );
    }

    return (
      <LongreadBlockWrapper>
        <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
          <PodcastIframeWrapper height={iframeHeight}>
            {videoType === 'mb' ? (
              <iframe
                width="380"
                height="380"
                className="mubert-iframe mubert-widget"
                src={iframeUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onError={handleIframeError}
              />
            ) : (
              <iframe
                width="100%"
                height={videoType === 'sc' ? '300' : '120'}
                scrolling={videoType === 'sc' ? 'no' : undefined}
                frameBorder={videoType === 'sc' ? 'no' : '0'}
                allow={videoType === 'sc' ? 'autoplay' : undefined}
                src={iframeUrl}
                seamless={videoType !== 'sc'}
                onError={handleIframeError}
                style={{ border: 0, background: 'transparent' }}
              />
            )}
          </PodcastIframeWrapper>
        </PodcastWrapper>
      </LongreadBlockWrapper>
    );
  }

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <LongreadBlockWrapper>
      <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
        <PodcastContent>
          <div>
            {title && (
              <PodcastTitle textColor={defaultTextColor}>
                <FormatLinkedText text={title} />
              </PodcastTitle>
            )}
            {podcastTime && (
              <PodcastDuration textColor={defaultTextColor}>{podcastTime}</PodcastDuration>
            )}
          </div>
          {audioUrl && !isPlayerVisible && (
            <PodcastListenButton onClick={handleListenClick} textColor={defaultTextColor}>
              Слушать
            </PodcastListenButton>
          )}
        </PodcastContent>

        {audioUrl && isPlayerVisible && (
          <PodcastPlayer textColor={defaultTextColor}>
            {/* src не задаём в JSX — им управляет useEffect через audio.dataset.loadedUrl */}
            <audio ref={audioRef} />
            <PodcastPlayerControls>
              <PodcastPlayerButton onClick={togglePlayPause} textColor={defaultTextColor}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </PodcastPlayerButton>
              <PodcastPlayerProgress onClick={handleProgressClick} textColor={defaultTextColor}>
                <div
                  style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    backgroundColor: '#FEFEFE',
                    transition: 'width 0.1s ease',
                  }}
                />
              </PodcastPlayerProgress>
              <PodcastPlayerTime textColor={defaultTextColor}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </PodcastPlayerTime>
            </PodcastPlayerControls>
          </PodcastPlayer>
        )}
      </PodcastWrapper>
    </LongreadBlockWrapper>
  );
};
import { FC, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { LongreadBlockWrapper } from '../../styles/LongreadBlockRenderer.styles';
import {
  PodcastWrapper,
  PodcastContent,
  PodcastTitle,
  PodcastDuration,
  PodcastListenButton,
  PodcastIframeWrapper,
  PodcastPlayer,
  PodcastPlayerControls,
  PodcastPlayerButton,
  PodcastPlayerProgress,
  PodcastPlayerTime,
} from '../../styles/LongreadBlockRenderer.styles';
import { FormatLinkedText } from 'brg-longread';
import { invertColor } from '../../utils/colorUtils';
import { Endpoints } from '@brg/api';

interface PodcastBlockProps {
  block: any;
  backgroundColor?: string;
}

const getAdditionalInfoVal = (additionalInfo: any[], key: string): string | null => {
  if (!Array.isArray(additionalInfo)) return null;
  return additionalInfo.find((item: any) => item.name === key)?.value ?? null;
};

const checkAudioType = (link: string): 'sc' | 'bc' | 'mb' | 'nodata' => {
  if (!link) return 'nodata';
  if (/bandcamp/i.test(link)) return 'bc';
  if (/sound/i.test(link)) return 'sc';
  if (/mubert/i.test(link)) return 'mb';
  return 'nodata';
};

const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};


const PlayIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 2l10 6-10 6V2z" />
  </svg>
);

const PauseIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M4 2h3v12H4V2zm5 0h3v12H9V2z" />
  </svg>
);


export const PodcastBlock: FC<PodcastBlockProps> = ({ block, backgroundColor }) => {
  const [iframeError, setIframeError] = useState(false);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const defaultTextColor = useMemo(
    () => (!backgroundColor ? '#FEFEFE' : invertColor(backgroundColor, '#FEFEFE', '#151514')),
    [backgroundColor]
  );

  const hasIframe = useMemo(
    () => Boolean(block.text?.includes('<iframe')),
    [block.text]
  );

  const { iframeUrl, videoType } = useMemo(() => {
    if (!hasIframe || !block.text) return { iframeUrl: null, videoType: 'nodata' as const };
    const match = block.text.match(/<iframe\s+.*?\s+src=(".*?").*?<\/iframe>/);
    return {
      iframeUrl: match ? match[1].slice(1, -1) : null,
      videoType: checkAudioType(block.text),
    };
  }, [block.text, hasIframe]);

  const title = useMemo((): string => {
    if (hasIframe && block.text) {
      const textWithoutIframe = block.text.replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');

      // 1) <a title="Название трека">
      const linkMatches = textWithoutIframe.match(/<a[^>]*title=["']([^"']+)["'][^>]*>/gi);
      if (linkMatches?.length) {
        const lastMatch = linkMatches[linkMatches.length - 1].match(/title=["']([^"']+)["']/);
        if (lastMatch?.[1]) return lastMatch[1].trim();
      }

      // 2) Текст внутри последней <a>...</a>
      const linkTextMatches = textWithoutIframe.match(/<a[^>]*>([^<]+)<\/a>/gi);
      if (linkTextMatches?.length) {
        const textMatch = linkTextMatches[linkTextMatches.length - 1].match(/>([^<]+)</);
        if (textMatch?.[1]) return textMatch[1].trim();
      }

      // 3) Весь текст без HTML
      return textWithoutIframe.replace(/<[^>]*>/g, '').trim();
    }

    if (block.attachments?.length) {
      const att = block.attachments.find((a: any) => a.files?.length);
      if (att) {
        const raw = att.text || att.description || '';
        return raw.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
      }
    }

    return '';
  }, [block.text, block.attachments, hasIframe]);

  const audioUrl = useMemo((): string => {
    if (hasIframe || !block.attachments?.length) return '';

    const files = block.attachments[0]?.files;
    if (!files?.length) return '';

    const mp3File = files.find(
      (f: any) => f?.url?.split('.').pop()?.toLowerCase() === 'mp3'
    );
    let baseUrl: string = (mp3File || files[0])?.url;
    if (!baseUrl) return '';

    if (/\.wav$/i.test(baseUrl)) baseUrl = baseUrl.replace(/\.wav$/i, '.mp3');

    const ext = baseUrl.split('.').pop()?.toLowerCase();
    if (!['mp3', 'wav'].includes(ext || '')) return '';

    return `${Endpoints.files}/${baseUrl}`;
  }, [block.attachments, hasIframe]);

  const podcastTime = useMemo((): string | null => {
    const val = getAdditionalInfoVal(block.additionalInfo || [], 'podcastTime');
    if (!val) return null;
    const total = Math.ceil(Number(val) / 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
  }, [block.additionalInfo]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl || !isPlayerVisible) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);

    if (audio.dataset.loadedUrl !== audioUrl) {
      audio.dataset.loadedUrl = audioUrl;
      audio.src = audioUrl;
      audio.load();
    }

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl, isPlayerVisible]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPlayerVisible) return;

    if (isPlaying) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  }, [isPlaying, isPlayerVisible]);


  const handleListenClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlayerVisible(true);
    setIsPlaying(true);
  }, []);

  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
  }, []);

  const handleIframeError = useCallback(() => setIframeError(true), []);
  const handleIframeRetry = useCallback(() => setIframeError(false), []);

  if (hasIframe) {
    if (!iframeUrl) return null;

    let iframeHeight = 300;
    if (videoType === 'bc') iframeHeight = 120;
    else if (videoType === 'mb') iframeHeight = 380;
    else if (videoType === 'sc') {
      const scReg = block.text?.match(/height=["']\s*(\d+)["']/);
      if (scReg) iframeHeight = Number(scReg[1]);
    }

    if (iframeError) {
      return (
        <LongreadBlockWrapper>
          <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
            <div style={{ color: defaultTextColor, padding: '20px', textAlign: 'center' }}>
              При загрузке контента произошла ошибка
              <button
                onClick={handleIframeRetry}
                style={{
                  marginTop: '10px',
                  display: 'block',
                  padding: '8px 16px',
                  border: `1px solid ${defaultTextColor}`,
                  background: 'transparent',
                  color: defaultTextColor,
                  cursor: 'pointer',
                  borderRadius: '4px',
                }}
              >
                Попробовать ещё раз
              </button>
            </div>
          </PodcastWrapper>
        </LongreadBlockWrapper>
      );
    }

    return (
      <LongreadBlockWrapper>
        <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
          <PodcastIframeWrapper height={iframeHeight}>
            {videoType === 'mb' ? (
              <iframe
                width="380"
                height="380"
                className="mubert-iframe mubert-widget"
                src={iframeUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onError={handleIframeError}
              />
            ) : (
              <iframe
                width="100%"
                height={videoType === 'sc' ? '300' : '120'}
                scrolling={videoType === 'sc' ? 'no' : undefined}
                frameBorder={videoType === 'sc' ? 'no' : '0'}
                allow={videoType === 'sc' ? 'autoplay' : undefined}
                src={iframeUrl}
                seamless={videoType !== 'sc'}
                onError={handleIframeError}
                style={{ border: 0, background: 'transparent' }}
              />
            )}
          </PodcastIframeWrapper>
        </PodcastWrapper>
      </LongreadBlockWrapper>
    );
  }

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <LongreadBlockWrapper>
      <PodcastWrapper style={{ backgroundColor: backgroundColor || '#151514' }}>
        <PodcastContent>
          <div>
            {title && (
              <PodcastTitle textColor={defaultTextColor}>
                <FormatLinkedText text={title} />
              </PodcastTitle>
            )}
            {podcastTime && (
              <PodcastDuration textColor={defaultTextColor}>{podcastTime}</PodcastDuration>
            )}
          </div>
          {audioUrl && !isPlayerVisible && (
            <PodcastListenButton onClick={handleListenClick} textColor={defaultTextColor}>
              Слушать
            </PodcastListenButton>
          )}
        </PodcastContent>

        {audioUrl && isPlayerVisible && (
          <PodcastPlayer textColor={defaultTextColor}>
            {/* src не задаём в JSX — им управляет useEffect через audio.dataset.loadedUrl */}
            <audio ref={audioRef} />
            <PodcastPlayerControls>
              <PodcastPlayerButton onClick={togglePlayPause} textColor={defaultTextColor}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </PodcastPlayerButton>
              <PodcastPlayerProgress onClick={handleProgressClick} textColor={defaultTextColor}>
                <div
                  style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    backgroundColor: '#FEFEFE',
                    transition: 'width 0.1s ease',
                  }}
                />
              </PodcastPlayerProgress>
              <PodcastPlayerTime textColor={defaultTextColor}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </PodcastPlayerTime>
            </PodcastPlayerControls>
          </PodcastPlayer>
        )}
      </PodcastWrapper>
    </LongreadBlockWrapper>
  );
};
